import { MuiMaterials } from "../../@index.js";
import { getWindowChromeThemeColor } from "./windowChromeTheme.js";

/**
 * The app's one true loading spinner. Wraps react-loader-spinner's RotatingLines so every
 * window paints the same dashed-arc animation instead of a mix of MUI CircularProgress
 * arcs and RotatingLines rings. Color follows the current light/dark theme automatically.
 *
 * Sizes:
 *   - size="page" / size="modal" — 48px, page/window load overlays (same as after Site Select Continue)
 *   - size="inline" — 22px, for input adornments and button start icons
 *   - size="tiny" — 14px, for card-corner status pips
 *   - a number/string is passed through to width for one-off sizing
 *
 * Pass `strokeColor` only when you need to force a color that differs from the theme text
 * (e.g. on a contained button that already has its own foreground color).
 */
const SIZE_PRESETS = {
    page: "48",
    modal: "48",
    inline: "22",
    tiny: "14",
};

export default function Spinner({
    size = "page",
    strokeColor = null,
    strokeWidth = "3",
    animationDuration = "0.72",
}) {
    const width = SIZE_PRESETS[size] ?? String(size);
    const color = strokeColor ?? getWindowChromeThemeColor("#333333", "#f4cc8e");
    return (
        <MuiMaterials.RotatingLines
            strokeColor={color}
            strokeWidth={strokeWidth}
            animationDuration={animationDuration}
            width={width}
            visible
        />
    );
}

/**
 * Full-viewport (within the window/modal chrome) centered loader used after Site Select
 * Continue and for inventory/versioning/etc. window loads. Absolute inset so the spinner
 * sits in the middle of the viewing area instead of the top of a scroll body.
 */
export function CenteredLoadingOverlay({
    size = "modal",
    strokeColor = null,
    sx = null,
    zIndex = 2,
}) {
    return (
        <MuiMaterials.Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: getWindowChromeThemeColor(
                    "rgba(232,232,232,0.7)",
                    "rgba(26,26,26,0.7)",
                ),
                pointerEvents: "auto",
                ...(sx || {}),
            }}
        >
            <Spinner size={size} strokeColor={strokeColor} />
        </MuiMaterials.Box>
    );
}
